import { clsx } from 'clsx';
import { t } from 'i18next';
import React, { use, useMemo } from 'react';
import { Tab as AriaTab, TabList, TabPanel, Tabs } from 'react-aria-components';
import Text from '~/components/typographies/Text';
import styles from './TabBar.module.scss';

export interface TabProps {
  label: string;
  id: string;
  children?: React.ReactNode;
  className?: string;
  panelClassName?: string;
  disabled?: boolean;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  notificationCount?: number;
}

type Variant = 'default' | 'boxed' | 'underlined';

type TabElement = React.ReactElement<TabProps>;
export interface TabBarProps {
  children: TabElement | (TabElement | null | undefined | false)[];
  variant?: Variant;
  className?: string;
  tabListClassName?: string;
  tabBarClassName?: string;
  selected?: string;
  defaultSelectedKey?: string;
  onSelect?: (tab: TabProps) => void;
  ref?: React.Ref<HTMLDivElement>;
}

export const Tab = (props: TabProps) => {
  const { id, className, disabled, label, Icon, iconClassName, notificationCount } = props;
  const context = use(TabBarContext);

  if (!context) {
    throw new Error('Tab must be used within a TabBar component');
  }

  const { variant, onSelect } = context;

  return (
    <AriaTab
      id={id}
      key={id}
      className={clsx(
        styles.tab,
        {
          [styles.boxed!]: variant === 'boxed',
          [styles.underlined!]: variant === 'underlined',
          [styles.default!]: variant === 'default',
        },
        className,
      )}
      isDisabled={disabled}
      onPress={() => onSelect?.(props)}
    >
      <span
        className={styles.tabLabel}
        data-intercom-target={label == t('section_label', { ns: 'payments' }) ? 'menu-conti-pagamenti' : undefined}
      >
        {label}
      </span>
      {Icon && <Icon className={clsx(styles.icon, iconClassName)} />}
      {notificationCount && notificationCount > 0 && (
        <div className={styles.notificationBadge}>
          <Text variant="caption" weight="semibold">
            {notificationCount}
          </Text>
        </div>
      )}
    </AriaTab>
  );
};
Tab.displayName = 'TabBar.Tab';

interface Context {
  variant: Variant;
  onSelect: TabBarProps['onSelect'];
}

const TabBarContext = React.createContext<Context | null>(null);

const getPanels = (
  children: TabBarProps['children'],
): { id: string; content: React.ReactNode; className?: string }[] => {
  return React.Children.toArray(children).flatMap((child) => {
    if (
      !React.isValidElement<TabProps>(child) ||
      (child.type as { displayName?: string }).displayName !== 'TabBar.Tab'
    ) {
      throw new Error('TabBar children must be of type TabBar.Tab');
    }

    // Navigation-only tabs (e.g. NavigationTabBar) carry no panel content —
    // their content is the routed page. Rendering an empty <TabPanel> would
    // make React Aria add tabIndex=0 to it, creating a focusable full-width
    // phantom tab stop under the tab list, so skip panels with no content.
    if (child.props.children == null) {
      return [];
    }

    return [{ id: child.props.id, content: child.props.children, className: child.props.panelClassName }];
  });
};

export const TabBar = ({
  className,
  tabListClassName,
  children,
  variant = 'default',
  selected,
  defaultSelectedKey,
  onSelect,
  ref,
}: TabBarProps) => {
  const panels = useMemo(() => getPanels(children), [children]);

  const contextValue: Context = useMemo(() => ({ variant, onSelect }), [variant, onSelect]);

  return (
    <TabBarContext.Provider value={contextValue}>
      <Tabs ref={ref} selectedKey={selected} defaultSelectedKey={defaultSelectedKey} className={className}>
        <TabList
          className={clsx(
            styles.tabs,
            {
              [styles.boxed!]: variant === 'boxed',
              [styles.underlined!]: variant === 'underlined',
              [styles.default!]: variant === 'default',
            },
            tabListClassName,
          )}
        >
          {children}
        </TabList>
        {panels.map((tab) => {
          return (
            <TabPanel key={tab.id} id={tab.id} className={tab.className}>
              {tab.content}
            </TabPanel>
          );
        })}
      </Tabs>
    </TabBarContext.Provider>
  );
};
